import type { Company, Permission, Role } from "@pentacode/core";
import type { ActivatableIconName, IconName, MenuGroup } from "@pentacode/ui/components";

/** One path segment of the app. A key starting with `:` is a path parameter, e.g. `:employeeId`. */
export interface RouteNode {
    label: string;
    icon?: ActivatableIconName;
    permission?: Permission;
    /** A permission check beyond `permission`; failing it disables the node like a missing permission. */
    permitted?: (access: RouteAccess) => boolean;
    available?: (access: RouteAccess) => boolean;
    /** Query params a menu link to this node adds, e.g. today's date. */
    linkParams?: () => Record<string, string>;
    /** Where a menu link to this node goes instead, relative to its parent, e.g. a default sub-page. */
    linkPath?: string;
    /** The menu group the node renders in; the main list when unset. */
    menuGroup?: "footer";
    /** Sub-menus disable a node the user lacks permission for; this hides it instead. */
    hideWhenInaccessible?: boolean;
    /** The group a sub-menu puts the node in; consecutive nodes sharing it render together. */
    menuSection?: MenuSection;
    /** Reachable by path but not listed in menus. */
    menuHidden?: boolean;
    children?: RouteTree;
}

export interface MenuSection {
    label?: string;
    icon?: IconName;
}

export interface RouteAccess {
    hasPermission(permission: Permission): boolean;
    embedded: boolean;
    dev: boolean;
    company: Pick<Company, "features" | "billing" | "country"> | null;
    role: Role | undefined;
    /** Owners and admins pass every permission check. */
    owner: boolean;
    /** The employee a page is about, on per-employee pages. */
    employee: { accessible: boolean; probation: boolean } | null;
}

export function canAccess(node: RouteNode, access: RouteAccess): boolean {
    return (node.available?.(access) ?? true) && isPermitted(node, access);
}

function isPermitted(node: RouteNode, access: RouteAccess): boolean {
    return (!node.permission || access.hasPermission(node.permission)) && (node.permitted?.(access) ?? true);
}

export type MenuItemState = "enabled" | "disabled" | "hidden";

/** A node that isn't available is hidden; one the user lacks permission for is disabled unless it hides. */
export function menuItemState(node: RouteNode, access: RouteAccess): MenuItemState {
    if (node.menuHidden || !(node.available?.(access) ?? true)) {
        return "hidden";
    }
    if (isPermitted(node, access)) {
        return "enabled";
    }
    return node.hideWhenInaccessible ? "hidden" : "disabled";
}

export type RouteTree = Readonly<Record<string, RouteNode>>;

export function defineRoutes<const T extends RouteTree>(tree: T): T {
    return tree;
}

export function routeKeys<T extends RouteTree>(tree: T): (keyof T & string)[] {
    return Object.keys(tree);
}

/** Every path in `T`, e.g. `"settings" | "settings/account" | "employees/:employeeId/core-data"`. */
export type RoutePath<T extends RouteTree> = {
    [K in keyof T & string]:
        | K
        | (NonNullable<T[K]["children"]> extends infer C extends RouteTree ? `${K}/${RoutePath<C>}` : never);
}[keyof T & string];

type ParamNames<P extends string> = P extends `${infer Head}/${infer Rest}`
    ? ParamNames<Head> | ParamNames<Rest>
    : P extends `:${infer Name}`
      ? Name
      : never;

export type RouteParams<P extends string> = { [Name in ParamNames<P>]: string | number };

/** The arguments after `path`: none for a static path, the params object when `path` has `:params`. */
export type RouteParamArgs<P extends string> = [ParamNames<P>] extends [never] ? [] : [params: RouteParams<P>];

export function routePath<const P extends string>(path: P, ...[params]: RouteParamArgs<P>): string {
    return path
        .split("/")
        .map((segment) => {
            if (!segment.startsWith(":")) {
                return segment;
            }
            const name = segment.slice(1);
            const value = (params as Record<string, string | number> | undefined)?.[name];
            if (value === undefined) {
                throw new Error(`routePath: missing :${name} in ${path}`);
            }
            return encodeURIComponent(String(value));
        })
        .join("/");
}

/** Splits `keys` into runs of consecutive nodes that share a `menuSection`. */
export function menuGroups<K extends string>(keys: K[], route: (key: K) => RouteNode): K[][] {
    const groups: K[][] = [];
    for (const key of keys) {
        const last = groups.at(-1);
        if (last && route(last[0]).menuSection === route(key).menuSection) {
            last.push(key);
        } else {
            groups.push([key]);
        }
    }
    return groups;
}

/** The menu data for `keys`: hidden nodes left out, unpermitted ones disabled, grouped by `menuSection`. */
export function menuData<K extends string>(keys: K[], route: (key: K) => RouteNode, access: RouteAccess): MenuGroup[] {
    const states = new Map(keys.map((key) => [key, menuItemState(route(key), access)]));
    const visible = keys.filter((key) => states.get(key) !== "hidden");
    return menuGroups(visible, route).map((group) => {
        const section = route(group[0]!).menuSection;
        return {
            label: section?.label,
            icon: section?.icon,
            items: group.map((key) => {
                const { label, icon } = route(key);
                return { id: key, label, icon, disabled: states.get(key) === "disabled" };
            }),
        };
    });
}
